import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, ChevronDown, User, LogOut, Lock } from 'lucide-react';
import { useAuth, roleHomePaths } from '../../context/AuthContext';
import { useFilter } from '../../context/FilterContext';
import { useTheme } from '../../context/ThemeContext';
import { UserRole } from '../../types';

const roleLabels: Record<UserRole, string> = {
  [UserRole.NATIONAL]: 'National / MNRE Admin',
  [UserRole.STATE]:    'State Agency',
  [UserRole.DISCOM]:   'DISCOM Admin',
  [UserRole.CONSUMER]: 'Consumer',
  [UserRole.VENDOR]:   'Vendor / EPC',
};

export default function Topbar() {
  const {
    currentRole, currentUser, setRole,
    isAdmin, adminViewingAs, setAdminViewingAs,
    serverUser, logout,
  } = useAuth();
  const { resetFilters } = useFilter();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [open, setOpen]           = useState(false);
  const [userOpen, setUserOpen]   = useState(false);

  const handleRoleSwitch = (role: UserRole) => {
    if (isAdmin) {
      setAdminViewingAs(role);
    } else {
      setRole(role);
    }
    resetFilters();
    navigate(roleHomePaths[role]);
    setOpen(false);
  };

  const handleLogout = async () => {
    setUserOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const isConsumer = currentRole === UserRole.CONSUMER;

  return (
    <header className="h-12 flex items-center px-4 border-b border-border bg-bg-primary z-50 flex-shrink-0 gap-4">

      {/* ── Logo ── */}
      <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
        <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${isConsumer ? 'bg-consumer-green' : 'bg-navy'}`}>
          <Sun size={15} className="text-white" />
        </div>
        <div className="hidden sm:flex items-baseline gap-1">
          <span className={`text-sm font-bold leading-tight ${isConsumer ? 'text-consumer-green' : 'text-navy'}`}>
            SMART™
          </span>
          <span className="text-xs text-text-secondary hidden md:inline">Platform</span>
          <span className="text-xs text-text-secondary hidden md:inline">by</span>
          <span className={`text-xs font-bold hidden md:inline ${isConsumer ? 'text-consumer-green' : 'text-navy'}`}>Avion</span>
        </div>
      </div>

      <div className="flex-1" />

      {/* ── Dark / Light toggle ── */}
      <button
        onClick={toggleTheme}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        className="w-8 h-8 flex items-center justify-center rounded-input border border-border bg-bg-secondary hover:bg-bg-tertiary transition-colors text-text-secondary hover:text-text-primary flex-shrink-0"
        aria-label="Toggle theme"
      >
        {isDark ? <Sun size={14} /> : <Moon size={14} />}
      </button>

      {/* ── Role Switcher (admin only) / Role Badge (non-admin) ── */}
      {isAdmin ? (
        <div className="relative">
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-input border text-xs font-medium transition-colors border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-800"
            title="Admin: switch viewing role"
          >
            <Lock size={10} className="text-amber-600 flex-shrink-0" />
            <span className="hidden sm:inline">{roleLabels[currentRole]}</span>
            <span className="sm:hidden">Role</span>
            <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-56 bg-bg-primary border border-border rounded-card shadow-lg z-50 py-1">
                <p className="text-xs text-text-tertiary px-3 py-1.5 border-b border-border">
                  Admin — View as role
                </p>
                {Object.values(UserRole).map(role => (
                  <button
                    key={role}
                    onClick={() => handleRoleSwitch(role)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-bg-secondary transition-colors
                      ${currentRole === role ? 'font-semibold text-accent bg-bg-tertiary' : 'text-text-primary'}`}
                  >
                    {roleLabels[role]}
                  </button>
                ))}
                {adminViewingAs && (
                  <div className="border-t border-border mt-1 pt-1">
                    <button
                      onClick={() => { setAdminViewingAs(null); setOpen(false); navigate('/national/overview'); }}
                      className="w-full text-left px-3 py-2 text-xs text-amber-700 hover:bg-amber-50 transition-colors font-medium"
                    >
                      ← Reset to Admin view
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        /* Non-admin: read-only role badge, no switching */
        <div
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-input border border-border bg-bg-secondary text-xs font-medium text-text-secondary cursor-default select-none"
          title="Role assigned by administrator"
        >
          <span className="hidden sm:inline">{roleLabels[currentRole]}</span>
          <span className="sm:hidden">Role</span>
        </div>
      )}

      {/* ── User menu ── */}
      <div className="relative flex-shrink-0">
        <button
          onClick={() => setUserOpen(o => !o)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isConsumer ? 'bg-consumer-green' : 'bg-navy'}`}>
            <User size={13} className="text-white" />
          </div>
          <div className="hidden md:block text-left">
            <p className="text-xs font-semibold text-text-primary leading-tight">
              {serverUser?.displayName ?? currentUser.name}
            </p>
            <p className="text-xs text-text-tertiary leading-tight">
              {isAdmin ? 'Administrator' : currentUser.org}
            </p>
          </div>
          <ChevronDown size={12} className={`hidden md:block text-text-tertiary transition-transform ${userOpen ? 'rotate-180' : ''}`} />
        </button>

        {userOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setUserOpen(false)} />
            <div className="absolute right-0 top-full mt-1 w-48 bg-bg-primary border border-border rounded-card shadow-lg z-50 py-1">
              <div className="px-3 py-2 border-b border-border">
                <p className="text-xs font-semibold text-text-primary">
                  {serverUser?.displayName ?? currentUser.name}
                </p>
                <p className="text-xs text-text-tertiary mt-0.5">
                  {serverUser?.username ? `@${serverUser.username}` : currentUser.org}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger hover:bg-red-50 transition-colors"
              >
                <LogOut size={13} />
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
